import { lazy, Suspense, useEffect, ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence } from "framer-motion";
import { useCartStore as __useCartStore } from "@/stores/cartStore";
import ScrollToTop from "./components/ScrollToTop";
import PageTransition from "./components/PageTransition";

// Retry wrapper for dynamic imports — handles stale cache after deploys
function lazyRetry(factory: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() => {
      // Force reload once to get fresh chunks
      const key = "retry-lazy-refresh";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem(key);
      return factory(); // second attempt after reload
    })
  );
}

// Only the homepage is eagerly loaded — everything else is lazy
import Index from "./pages/Index";

// Lazy-loaded routes with retry
const Bags = lazyRetry(() => import("./pages/Bags"));
const Clothing = lazyRetry(() => import("./pages/Clothing"));
const Accessories = lazyRetry(() => import("./pages/Accessories"));
const Shop = lazyRetry(() => import("./pages/Shop"));
const ProductDetail = lazyRetry(() => import("./pages/ProductDetail"));
const Atelier = lazyRetry(() => import("./pages/Atelier"));
const About = lazyRetry(() => import("./pages/About"));
const Workshops = lazyRetry(() => import("./pages/Workshops"));
const Contact = lazyRetry(() => import("./pages/Contact"));
const FAQ = lazyRetry(() => import("./pages/FAQ"));
const Cart = lazyRetry(() => import("./pages/Cart"));
const Wishlist = lazyRetry(() => import("./pages/Wishlist"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const ShippingReturns = lazyRetry(() => import("./pages/ShippingReturns"));
const Care = lazyRetry(() => import("./pages/Care"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const BlogPost = lazyRetry(() => import("./pages/BlogPost"));
const SizeFit = lazyRetry(() => import("./pages/SizeFit"));
const Sale = lazyRetry(() => import("./pages/Sale"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));

// Lazy-loaded non-critical global components
const NewsletterPopup = lazyRetry(() => import("./components/NewsletterPopup"));
const CookieConsent = lazyRetry(() => import("./components/CookieConsent"));
const BackToTop = lazyRetry(() => import("./components/BackToTop"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-border border-t-foreground rounded-full animate-spin" />
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/bags" element={<PageTransition><Suspense fallback={<RouteFallback />}><Bags /></Suspense></PageTransition>} />
        <Route path="/clothing" element={<PageTransition><Suspense fallback={<RouteFallback />}><Clothing /></Suspense></PageTransition>} />
        <Route path="/accessories" element={<PageTransition><Suspense fallback={<RouteFallback />}><Accessories /></Suspense></PageTransition>} />
        <Route path="/shop" element={<PageTransition><Suspense fallback={<RouteFallback />}><Shop /></Suspense></PageTransition>} />
        <Route path="/product/:id" element={<PageTransition><Suspense fallback={<RouteFallback />}><ProductDetail /></Suspense></PageTransition>} />
        <Route path="/atelier" element={<PageTransition><Suspense fallback={<RouteFallback />}><Atelier /></Suspense></PageTransition>} />
        <Route path="/about" element={<PageTransition><Suspense fallback={<RouteFallback />}><About /></Suspense></PageTransition>} />
        <Route path="/values" element={<Navigate to="/about" replace />} />
        <Route path="/workshops" element={<PageTransition><Suspense fallback={<RouteFallback />}><Workshops /></Suspense></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Suspense fallback={<RouteFallback />}><Contact /></Suspense></PageTransition>} />
        <Route path="/faq" element={<PageTransition><Suspense fallback={<RouteFallback />}><FAQ /></Suspense></PageTransition>} />
        <Route path="/cart" element={<PageTransition><Suspense fallback={<RouteFallback />}><Cart /></Suspense></PageTransition>} />
        <Route path="/wishlist" element={<PageTransition><Suspense fallback={<RouteFallback />}><Wishlist /></Suspense></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><Suspense fallback={<RouteFallback />}><Privacy /></Suspense></PageTransition>} />
        <Route path="/terms" element={<PageTransition><Suspense fallback={<RouteFallback />}><Terms /></Suspense></PageTransition>} />
        <Route path="/shipping-returns" element={<PageTransition><Suspense fallback={<RouteFallback />}><ShippingReturns /></Suspense></PageTransition>} />
        <Route path="/care" element={<PageTransition><Suspense fallback={<RouteFallback />}><Care /></Suspense></PageTransition>} />
        <Route path="/blog" element={<PageTransition><Suspense fallback={<RouteFallback />}><Blog /></Suspense></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><Suspense fallback={<RouteFallback />}><BlogPost /></Suspense></PageTransition>} />
        <Route path="/size-fit" element={<PageTransition><Suspense fallback={<RouteFallback />}><SizeFit /></Suspense></PageTransition>} />
        <Route path="/sale" element={<PageTransition><Suspense fallback={<RouteFallback />}><Sale /></Suspense></PageTransition>} />
        <Route path="*" element={<PageTransition><Suspense fallback={<RouteFallback />}><NotFound /></Suspense></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const AppContent = () => {
  const syncCart = __useCartStore(state => state.syncCart);
  useEffect(() => {
    syncCart();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncCart();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [syncCart]);

  return (
    <>
      <ScrollToTop />
      <AnimatedRoutes />
      <Suspense fallback={null}>
        <NewsletterPopup />
      </Suspense>
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <Suspense fallback={null}>
        <BackToTop />
      </Suspense>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
